import React, { createContext, useState, useEffect, useContext } from 'react';
import UserContext from './UserContext'; // Adjust path if needed
import { useDealership } from './DealershipContext';
import { calculateLineItemTotals } from '../utils/pricing';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {

  const { user } = useContext(UserContext);
  const { dealership, loading: dealershipLoading, dealershipFetchAttempted, renamedBacs } = useDealership();

  // • Base empty cart shape
  const EMPTY_CART = {
    name: '',
    email: '',
    phone: '',
    message: '',
    status: 'DRAFT',
    lineItems: [],
    orderTotal: 0,
    dealershipBac: null,
  };

  const [cartState, setCartState] = useState(() => {
    // Initialize state from localStorage
    const savedCart = localStorage.getItem('cart');
    const parsedCart = savedCart ? JSON.parse(savedCart) : {};
    const lineItems = (parsedCart.lineItems || []).map(item => {
      const isCustomAd =
        item.name === "Custom Ad Request" ||
        item.name === "CustomAdRequest" ||
        item.name?.includes("Custom Ad");
      if (isCustomAd) {
        return {
          ...item,
          adType: item.adType || '',
          adSize: item.adSize || '',
          numVehicles: item.numVehicles || '',
          offerType: item.offerType || '',
          itemId: item.itemId,
          quantity: Number(item.quantity) || 1,
          price: Number(item.price) || 0,
          fee: Number(item.fee) || 0,
          markup: Number(item.markup) || 0,
          total: Number(item.total) || 0,
          employeeInfo: !!item.employeeInfo
        };
      }
      const normalizedItem = {
        ...item,
        itemId: Number(item.itemId) || null,
        variantId: Number(item.variantId) || null,
        fee: Number(item.fee) || 0,
        packageId: Number(item.packageId) || null,
        quantity: Number(item.quantity) || 1,
        price: Number(item.price) || 0,
        markup: Number(item.markup) || 0,
        total: Number(item.total) || 0,
        employeeId: Number(item.employeeId) || null
      };
      const pricing = calculateLineItemTotals({
        price: normalizedItem.price,
        fee: normalizedItem.fee,
        quantity: normalizedItem.quantity,
      });
      return { ...normalizedItem, ...pricing };
    });
    const orderTotal = lineItems.reduce((total, item) => total + (Number(item.total) || 0), 0);
    return {
      name: parsedCart.name || '',
      email: parsedCart.email || '',
      phone: parsedCart.phone || '',
      message: parsedCart.message || '',
      status: parsedCart.status || 'DRAFT',
      dealershipBac: parsedCart.dealershipBac || parsedCart.dealershipId || null,
      lineItems,
      orderTotal
    };
  });
  useEffect(() => {
    if (!user) {
      clearCart();
    }
    // eslint-disable-next-line
  }, [user]);

  // Persist cart state
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartState));
  }, [cartState]);
 // • Clear or reassign cart when dealership changes
  useEffect(() => {
    // While the active dealership is still being resolved (fetch not yet
    // attempted or in flight), leave the persisted cart untouched — clearing
    // here wiped the cart on every full page load.
    if (user && (!dealershipFetchAttempted || dealershipLoading)) {
      return;
    }
    if (!dealership?.bac) {
      setCartState(EMPTY_CART);
      return;
    }
    setCartState(prev => {
      // A dealership whose BAC was just corrected is the same dealership:
      // keep what is in the cart and carry it over to the new BAC.
      const renamedTo = prev.dealershipBac ? renamedBacs?.[String(prev.dealershipBac)] : undefined;
      const sameDealership = renamedTo !== undefined && String(renamedTo) === String(dealership.bac);
      if (prev.dealershipBac && prev.dealershipBac !== dealership.bac && !sameDealership) {
        // Different dealership -> reset cart
        return { ...EMPTY_CART, dealershipBac: dealership.bac };
      }
      return { ...prev, dealershipBac: dealership.bac };
    });
  }, [dealership?.bac, user, dealershipFetchAttempted, dealershipLoading, renamedBacs]);

  // Add item to cart with logging
  const addToCart = (item) => {
    const isCustomAd =
      item.name === "Custom Ad Request" ||
      item.name === "CustomAdRequest" ||
      item.name?.includes("Custom Ad"); 
    let lineItem;
    if (isCustomAd) {
      lineItem = {
        name: item.name,
        image: item.image,
        adType: item.adType || '',
        adSize: item.adSize || '',
        numVehicles: item.numVehicles || '',
        offerType: item.offerType || '',
        notes: item.notes || '',
        itemId: item.itemId,
        quantity: Number(item.quantity) || 1,
        price: Number(item.price) || 0,
        fee: Number(item.fee) || 0,
        markup: Number(item.markup) || 0,
        total: Number(item.total) || 0,
        employeeId: Number(item.employeeId) || null // Logging addition
      };
    } else {
      const pricing = calculateLineItemTotals({
        price: item.price,
        fee: item.fee,
        quantity: item.quantity,
      });
      lineItem = {
        itemId: Number(item.itemId) || null,
        packageId: Number(item.packageId) || null,
        variantId: Number(item.variantId) || null,
        quantity: pricing.quantity,
        price: pricing.price,
        fee: pricing.fee,
        markup: pricing.markup,
        total: pricing.total,
        employeeId: Number(item.employeeId) || null,
        name: item.name,
        image: item.image,
        packageName: item.packageName || '',
        variantName: item.variantName || ''
      };
    }
    const newTotal = (cartState.orderTotal || 0) + (lineItem.total || 0);

    setCartState(prevState => ({
      ...prevState,
      lineItems: [...(prevState.lineItems || []), lineItem],
      orderTotal: newTotal
    }));
  };

  // Remove from cart with logging
  const removeFromCart = (index) => {
    const updatedLineItems = [...cartState.lineItems];
    updatedLineItems.splice(index, 1);
    const newTotal = updatedLineItems.reduce((total, item) => total + (item.total || 0), 0);

    setCartState(prevState => ({
      ...prevState,
      lineItems: updatedLineItems,
      orderTotal: newTotal
    }));
  };

  // Update quantity with logging and error safety
  const updateQuantity = (index, quantity) => {
    setCartState(prevState => {
      const updatedLineItems = [...prevState.lineItems];
      const item = updatedLineItems[index];
      if (!item) {
        return prevState;
      }
      const price = Number(item.price);
      const fee = Number(item.fee);

      if (isNaN(price) || isNaN(fee)) {
        // invalid price or fee
      }
      if (price === 0 && (!item.packageId || !item.packageName)) {
        // item price zero with no package
      }

      const pricing = calculateLineItemTotals({ price, fee, quantity });
      updatedLineItems[index] = {
        ...item,
        quantity: pricing.quantity,
        markup: pricing.markup,
        total: pricing.total
      };
      const orderTotal = updatedLineItems.reduce((t, i) => t + (Number(i.total) || 0), 0);

      return { ...prevState, lineItems: updatedLineItems, orderTotal };
    });
  };

  const updateOrderDetails = (details) => {
    setCartState(prevState => ({
      ...prevState,
      ...details
    }));
  };

  const clearCart = () => {
    setCartState({ ...EMPTY_CART, dealershipBac: dealership?.bac || null });
    localStorage.removeItem('cart');
  };

  return (
    <CartContext.Provider
      value={{
        ...cartState,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        updateOrderDetails
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Custom hook for using the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartProvider;
